import { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { SUPPORTED_TIMEZONES, timezoneAbbr } from '../lib/timezones';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { UsersAdminTab } from './UsersAdminTab';
import { SlaPolicyEditor } from './SlaPolicyEditor';
import { SuppressionRulesEditor } from './SuppressionRulesEditor';

function getApiBase(): string {
  // Relative URL — the Replit proxy routes `/api` to the API artifact in both
  // dev and prod (see artifacts/api-server/.replit-artifact/artifact.toml).
  // Hardcoding `:8080` would break in production where only port 80 is public.
  // Mirrors lib/apiClient.ts#getApiBase.
  return '/api';
}

interface SettingsPanelProps {
  onClose: () => void;
}

type SettingsTab = 'customers' | 'users' | 'sla' | 'suppression' | 'entra' | 'ai' | 'threatintel' | 'operational';

// Settings is split by role:
//   Developer: full Platform Settings, including Azure/AI/TI configuration.
//   Admin: reduced Administration Settings, focused on customers, users,
//          SLA and suppression. Audit review now lives in Log Master. Platform secrets remain hidden.
// SOC Manager/L1/L2/Customer Viewer do not see the settings entry point.
const ALL_TABS: { id: SettingsTab; label: string; adminOnly?: boolean; developerOnly?: boolean }[] = [
  { id: 'customers',   label: 'Customers' },
  { id: 'users',       label: 'SOC Users', adminOnly: true },
  { id: 'sla',         label: 'SLA Policies', adminOnly: true },
  { id: 'suppression', label: 'Suppression', adminOnly: true },
  { id: 'entra',       label: 'Entra ID', developerOnly: true },
  { id: 'ai',          label: 'AI / Foundry', developerOnly: true },
  { id: 'threatintel', label: 'Threat Intel', developerOnly: true },
  { id: 'operational', label: 'Operational', adminOnly: true },
];

interface CustomerRecord {
  id: string;
  name: string;
  tenantId: string;
  clientId?: string;
  workspaceId: string;
  workspaceName?: string;
  subscription?: string;
  resourceGroup?: string;
  color?: string;
  logoUrl?: string;
  logoFileName?: string;
  logoUpdatedAt?: string | null;
  enabled: boolean;
  isDefault?: boolean;
  hasSecret?: boolean;
  lighthouseMode?: boolean;
  /** IANA timezone (e.g. "Asia/Kuala_Lumpur"). Drives daily-report window + UI formatting. */
  timezone?: string;
  /** Autonomous Sentinel-Incident Triage Agent mode for this customer. */
  triageMode?: 'off' | 'recommend' | 'auto_close_high_confidence';
  /** Minimum confidence (50-100) required before the triage agent will
   *  auto-close FALSE/BENIGN POSITIVE incidents. Only meaningful when
   *  triageMode === 'auto_close_high_confidence'. */
  triageAutoCloseMinConfidence?: number;
  /** SOC L1 Auto-Investigation autonomous mode. 'on' opts the customer
   *  into the customer-walking sweeper that posts AI RCAs back to Sentinel
   *  for every new/active incident, independent of dashboard selection. */
  autoInvestigateMode?: 'off' | 'on';
}

const CUSTOMER_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];

const BLANK_CUSTOMER: Omit<CustomerRecord, 'id' | 'enabled' | 'isDefault' | 'hasSecret'> & { clientSecret: string } = {
  name: '', tenantId: '', clientId: '', clientSecret: '', workspaceId: '',
  workspaceName: '', subscription: '', resourceGroup: '', color: '#3b82f6', lighthouseMode: false,
  timezone: 'UTC',
  triageMode: 'off',
  triageAutoCloseMinConfidence: 90,
  autoInvestigateMode: 'off',
};

const DEFAULT_FORM = {
  tenantId: '',
  clientId: '',
  clientSecret: '',
  redirectUri: '',
  corsOrigins: '',
  adminUsers: '',
  escalationEmail: '',
  foundryEndpoint: '',
  foundryKey: '',
  aiModel: 'gpt-4o',
  vtKey: '',
  abuseipdbKey: '',
  talosKey: '',
  otxKey: '',
  spurKey: '',
  rostiKey: '',
  inviteEmailEnabled: 'false',
  inviteSenderMailbox: '',
  inviteSenderDisplayName: 'KloudSecure SOC',
  inviteReplyTo: '',
  invitePortalUrl: '',
  inviteBrandName: 'KloudSecure',
};

type FormState = typeof DEFAULT_FORM;

export function SettingsPanel({ onClose }: SettingsPanelProps) {
  const { user } = useAuth();
  const isDeveloper = user?.role === 'developer';
  const isAdminRole = user?.role === 'admin';
  const canViewSettings = isDeveloper || isAdminRole;
  const canEditPlatformSettings = isDeveloper;
  const canEditAdminSettings = isDeveloper || isAdminRole;
  // `isAdmin` is intentionally true for Developer too so existing admin-safe
  // components such as SOC Users, Audit, SLA and Suppression remain available.
  const isAdmin = canEditAdminSettings;
  const TABS = ALL_TABS.filter((t) => {
    if (!canViewSettings) return false;
    if (t.developerOnly && !canEditPlatformSettings) return false;
    if (t.adminOnly && !canEditAdminSettings) return false;
    return true;
  });
  const [activeTab, setActiveTab] = useState<SettingsTab>('customers');
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);

  // ── CUSTOMERS STATE — backed by shared CustomerContext ──
  const { customers: ctxCustomers, refresh: refreshCustomers } = useCustomers();
  const [localCustomers, setLocalCustomers] = useState<CustomerRecord[]>([]);
  const [editingCustomer, setEditingCustomer] = useState<(typeof BLANK_CUSTOMER & { id?: string }) | null>(null);
  const [custSaving, setCustSaving] = useState(false);
  const [custTesting, setCustTesting] = useState<string | null>(null);
  const [custTestResult, setCustTestResult] = useState<Record<string, { ok: boolean; msg: string }>>({});
  const [custError, setCustError] = useState<string>('');
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoError, setLogoError] = useState<string>('');

  // Mirror context customers into local state (allows optimistic updates)
  useEffect(() => {
    setLocalCustomers(ctxCustomers as CustomerRecord[]);
  }, [ctxCustomers]);

  // Convenience alias so existing UI code keeps working
  const customers = localCustomers;
  function setCustomers(fn: (prev: CustomerRecord[]) => CustomerRecord[]) {
    setLocalCustomers(fn);
  }

  async function saveCustomer() {
    if (!editingCustomer) return;
    if (!editingCustomer.name.trim()) { setCustError('Name is required'); return; }
    setCustSaving(true); setCustError('');
    try {
      const isNew = !editingCustomer.id;
      const url = isNew ? `${getApiBase()}/customers` : `${getApiBase()}/customers/${editingCustomer.id}`;
      const resp = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editingCustomer),
      });
      const data = await resp.json() as { customer?: CustomerRecord; error?: string };
      if (!resp.ok) { setCustError(data.error ?? 'Save failed'); return; }
      const updated = data.customer!;
      // Optimistic update + global sync
      setLocalCustomers(prev => isNew ? [...prev, updated as CustomerRecord] : prev.map(c => c.id === updated.id ? updated as CustomerRecord : c));
      setEditingCustomer(null);
      await refreshCustomers(); // broadcast to all tabs immediately
    } catch (e) { setCustError(String(e)); }
    finally { setCustSaving(false); }
  }

  async function deleteCustomer(id: string) {
    if (!confirm('Delete this customer?')) return;
    // Snapshot for revert-on-failure, then optimistic remove.
    const snapshot = localCustomers;
    setLocalCustomers(prev => prev.filter(c => c.id !== id));
    try {
      const resp = await fetch(`${getApiBase()}/customers/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({} as { error?: string }));
        setLocalCustomers(snapshot); // revert
        setCustError(body.error ?? `Delete failed (HTTP ${resp.status})`);
        return;
      }
      await refreshCustomers(); // broadcast to all tabs
    } catch (e) {
      setLocalCustomers(snapshot); // revert
      setCustError(String(e));
    }
  }

  async function testCustomer(id: string) {
    setCustTesting(id);
    setCustTestResult(r => ({ ...r, [id]: { ok: false, msg: 'Testing…' } }));
    try {
      const resp = await fetch(`${getApiBase()}/customers/${id}/test`, { method: 'POST', credentials: 'include' });
      const d = await resp.json() as { ok: boolean; message?: string; error?: string };
      setCustTestResult(r => ({ ...r, [id]: { ok: d.ok, msg: d.message ?? d.error ?? (d.ok ? 'Connected' : 'Failed') } }));
    } catch (e) { setCustTestResult(r => ({ ...r, [id]: { ok: false, msg: String(e) } })); }
    finally { setCustTesting(null); }
  }

  async function uploadCustomerLogo(file: File) {
    if (!editingCustomer?.id) { setLogoError('Save the customer first, then upload a logo.'); return; }
    setLogoError('');
    const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/webp']);
    const lowerName = file.name.toLowerCase();
    const allowedExt = lowerName.endsWith('.png') || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') || lowerName.endsWith('.webp');
    if (!allowedTypes.has(file.type) || !allowedExt) {
      setLogoError('Logo must be PNG, JPG/JPEG, or WebP.');
      return;
    }
    if (file.size > 1024 * 1024) {
      setLogoError('Logo file size must be 1 MB or smaller.');
      return;
    }
    setLogoSaving(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read logo file'));
        reader.readAsDataURL(file);
      });
      const dataBase64 = dataUrl.split(',')[1] ?? '';
      const resp = await fetch(`${getApiBase()}/customers/${editingCustomer.id}/logo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fileName: file.name, mimeType: file.type, dataBase64 }),
      });
      const data = await resp.json().catch(() => ({} as { customer?: CustomerRecord; error?: string }));
      if (!resp.ok) { setLogoError(data.error ?? `Logo upload failed (HTTP ${resp.status})`); return; }
      if (data.customer) {
        setEditingCustomer((current) => current ? { ...current, ...data.customer } : current);
        setLocalCustomers((prev) => prev.map((customer) => customer.id === data.customer?.id ? data.customer as CustomerRecord : customer));
        await refreshCustomers();
      }
    } catch (e) {
      setLogoError(String(e));
    } finally {
      setLogoSaving(false);
    }
  }

  async function removeCustomerLogo() {
    if (!editingCustomer?.id) return;
    if (!window.confirm('Remove this customer logo?')) return;
    setLogoSaving(true);
    setLogoError('');
    try {
      const resp = await fetch(`${getApiBase()}/customers/${editingCustomer.id}/logo`, { method: 'DELETE', credentials: 'include' });
      const data = await resp.json().catch(() => ({} as { customer?: CustomerRecord; error?: string }));
      if (!resp.ok) { setLogoError(data.error ?? `Logo removal failed (HTTP ${resp.status})`); return; }
      if (data.customer) {
        setEditingCustomer((current) => current ? { ...current, ...data.customer } : current);
        setLocalCustomers((prev) => prev.map((customer) => customer.id === data.customer?.id ? data.customer as CustomerRecord : customer));
        await refreshCustomers();
      }
    } catch (e) {
      setLogoError(String(e));
    } finally {
      setLogoSaving(false);
    }
  }
  const [secretsVisible, setSecretsVisible] = useState<Record<string, boolean>>({});
  const [configLoaded, setConfigLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [aiTestStatus, setAiTestStatus] = useState<'idle' | 'testing' | 'success' | 'error' | 'warn'>('idle');
  const [aiTestMsg, setAiTestMsg] = useState('');
  const [secretsConfigured, setSecretsConfigured] = useState<Record<string, boolean>>({});
  const [configLoadError, setConfigLoadError] = useState<string>('');

  const settingsTitle = isDeveloper ? 'Platform Settings' : 'Administration Settings';
  const settingsDescription = isDeveloper
    ? 'Manage platform integrations, secure configuration and operational settings for the KloudSecure AI SOC Dashboard.'
    : 'Manage SOC administration settings such as customers, users, audit, SLA and suppression controls.';
  const azureOk = canEditPlatformSettings && configLoaded && !!form.clientId;

  useEffect(() => {
    const firstTab = TABS[0]?.id;
    if (firstTab && !TABS.some((t) => t.id === activeTab)) {
      setActiveTab(firstTab);
    }
  }, [TABS, activeTab]);


  useEffect(() => {
    if (!canEditPlatformSettings) {
      setConfigLoadError('');
      setConfigLoaded(true);
      return;
    }

    const ac = new AbortController();
    (async () => {
      try {
        const r = await fetch(`${getApiBase()}/config`, { credentials: 'include', signal: ac.signal });
        if (!r.ok) {
          setConfigLoadError(`Failed to load config (HTTP ${r.status})`);
          setConfigLoaded(true);
          return;
        }
        const d = await r.json() as Record<string, unknown>;
        setForm(f => ({
          ...f,
          tenantId:              String(d.tenantId              ?? f.tenantId),
          clientId:              String(d.clientId              ?? f.clientId),
          clientSecret:          d.clientSecretConfigured ? '••••(saved)••••' : f.clientSecret,
          redirectUri:           String(d.redirectUri           ?? f.redirectUri),
          corsOrigins:           String(d.corsOrigins           ?? f.corsOrigins),
          adminUsers:            String(d.adminUsers            ?? f.adminUsers),
          escalationEmail:       String(d.escalationEmail       ?? f.escalationEmail),
          foundryEndpoint:       String(d.foundryEndpoint       ?? f.foundryEndpoint),
          foundryKey:            d.foundryKeyConfigured ? '••••(saved)••••' : f.foundryKey,
          aiModel:               String(d.aiModel               ?? f.aiModel),
          vtKey:                 d.vtConfigured       ? '••••(saved)••••' : f.vtKey,
          abuseipdbKey:          d.abuseipdbConfigured ? '••••(saved)••••' : f.abuseipdbKey,
          talosKey:              d.talosConfigured    ? '••••(saved)••••' : f.talosKey,
          otxKey:                d.otxConfigured      ? '••••(saved)••••' : f.otxKey,
          spurKey:               d.spurConfigured     ? '••••(saved)••••' : f.spurKey,
          rostiKey:              d.rostiConfigured    ? '••••(saved)••••' : f.rostiKey,
          inviteEmailEnabled:    String(d.inviteEmailEnabled    ?? f.inviteEmailEnabled),
          inviteSenderMailbox:   String(d.inviteSenderMailbox   ?? f.inviteSenderMailbox),
          inviteSenderDisplayName: String(d.inviteSenderDisplayName ?? f.inviteSenderDisplayName),
          inviteReplyTo:         String(d.inviteReplyTo         ?? f.inviteReplyTo),
          invitePortalUrl:       String(d.invitePortalUrl       ?? f.invitePortalUrl),
          inviteBrandName:       String(d.inviteBrandName       ?? f.inviteBrandName),
        }));
        setSecretsConfigured({
          clientSecret:  Boolean(d.clientSecretConfigured),
          foundryKey:    Boolean(d.foundryKeyConfigured),
          vtKey:         Boolean(d.vtConfigured),
          abuseipdbKey:  Boolean(d.abuseipdbConfigured),
          talosKey:      Boolean(d.talosConfigured),
          otxKey:        Boolean(d.otxConfigured),
          spurKey:       Boolean(d.spurConfigured),
          rostiKey:      Boolean(d.rostiConfigured),
        });
        setConfigLoaded(true);
      } catch (e) {
        if ((e as { name?: string })?.name === 'AbortError') return; // unmounted
        setConfigLoadError(`Failed to load config: ${String(e)}`);
        setConfigLoaded(true);
      }
    })();
    return () => ac.abort();
  }, [canEditPlatformSettings]);

  function set(key: keyof FormState, value: string | boolean) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function toggleSecret(key: string) {
    setSecretsVisible(s => ({ ...s, [key]: !s[key] }));
  }

  async function handleSave() {
    if (!canEditPlatformSettings) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      return;
    }
    setSaveStatus('saving');
    try {
      const resp = await fetch(`${getApiBase()}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!resp.ok) throw new Error('Server error');
      setSaveStatus('success');
      // Also persist non-secret prefs to localStorage as a backup
      const localPrefs = { aiModel: form.aiModel };
      localStorage.setItem('soc-settings', JSON.stringify(localPrefs));
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  }

  async function handleTestConnection() {
    if (!canEditPlatformSettings) {
      setTestStatus('error');
      setTimeout(() => setTestStatus('idle'), 3000);
      return;
    }
    setTestStatus('testing');
    try {
      const resp = await fetch(`${getApiBase()}/config/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenantId: form.tenantId, clientId: form.clientId }),
      });
      setTestStatus(resp.ok ? 'success' : 'error');
    } catch {
      setTestStatus('success');
    }
    setTimeout(() => setTestStatus('idle'), 5000);
  }

  async function handleTestAI() {
    if (!canEditPlatformSettings) {
      setAiTestStatus('error');
      setAiTestMsg('Platform configuration is restricted to the Developer role.');
      setTimeout(() => { setAiTestStatus('idle'); setAiTestMsg(''); }, 5000);
      return;
    }
    setAiTestStatus('testing');
    setAiTestMsg('');
    try {
      const resp = await fetch(`${getApiBase()}/config/test-ai`, { method: 'POST', credentials: 'include' });
      const data = await resp.json() as { ok: boolean; message: string; deployments?: string; noDeployments?: boolean; wrongDeployment?: boolean };
      if (data.ok) {
        setAiTestStatus('success');
      } else if (data.noDeployments || data.wrongDeployment) {
        setAiTestStatus('warn');
      } else {
        setAiTestStatus('error');
      }
      setAiTestMsg(data.message);
    } catch (e) {
      setAiTestStatus('error');
      setAiTestMsg(String(e));
    }
    setTimeout(() => { setAiTestStatus('idle'); setAiTestMsg(''); }, 10000);
  }

  function renderSecretField(label: string, key: keyof FormState, placeholder: string) {
    const isVisible = secretsVisible[key];
    const value = form[key] as string;
    const isSaved = secretsConfigured[key];
    return (
      <div>
        <label className="block text-xs font-semibold text-muted-foreground mb-1">
          {label}
          {isSaved && <span className="ml-2 text-[10px] font-semibold text-green-600 dark:text-green-400">configured</span>}
        </label>
        <div className="relative">
          <input
            type={isVisible ? 'text' : 'password'}
            value={value}
            onChange={e => set(key, e.target.value)}
            placeholder={placeholder}
            className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm pr-20 focus:outline-none focus:ring-1 focus:ring-primary font-mono"
          />
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => toggleSecret(key as string)}
              className="text-xs text-muted-foreground hover:text-foreground px-1.5 py-0.5"
              title={isVisible ? 'Hide' : 'Show'}
            >
              {isVisible ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
            <span className="flex items-center text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded font-semibold gap-0.5" title="Not stored in plaintext"><Lock size={10} />Enc.</span>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Enter a new value to update. Leave unchanged to keep the existing secret.</p>
      </div>
    );
  }

  function renderField(label: string, key: keyof FormState, placeholder: string, opts?: { mono?: boolean; disabled?: boolean }) {
    return (
      <div>
        <label className="block text-xs font-semibold text-muted-foreground mb-1">{label}</label>
        <input
          value={form[key] as string}
          onChange={e => set(key, e.target.value)}
          placeholder={placeholder}
          disabled={opts?.disabled}
          className={`w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary
            ${opts?.mono ? 'font-mono text-xs' : ''}
            ${opts?.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
      </div>
    );
  }

  function renderSelect(label: string, key: keyof FormState, options: { value: string; label: string }[]) {
    return (
      <div>
        <label className="block text-xs font-semibold text-muted-foreground mb-1">{label}</label>
        <select
          value={form[key] as string}
          onChange={e => set(key, e.target.value)}
          className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }

  const saveBtnLabel =
    saveStatus === 'saving' ? 'Saving…' :
    saveStatus === 'success' ? 'Saved!' :
    saveStatus === 'error'   ? 'Failed, retry' :
    'Save Settings';

  if (!canViewSettings) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-6 px-4"
        onClick={onClose}
      >
        <div
          className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-foreground">Settings unavailable</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Dashboard Settings is limited to Developer and Administrator roles.
              </p>
            </div>
            <button onClick={onClose} className="text-2xl text-muted-foreground hover:text-foreground leading-none">&times;</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-6 px-4"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-3xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">{settingsTitle}</h2>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{settingsDescription}</span>
              {isDeveloper && (
                <span>Configuration source: <span className="font-semibold text-blue-600 dark:text-blue-400">KloudSecure API + Azure Key Vault + Azure PostgreSQL</span></span>
              )}
              {isDeveloper && !configLoaded && <span className="italic">Loading…</span>}
              {isDeveloper && configLoadError && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-[10px] font-semibold"
                  title={configLoadError}
                >
                  Config load failed
                </span>
              )}
              {isDeveloper && azureOk && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-[10px] font-semibold">
                  Azure credentials detected
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-2xl text-muted-foreground hover:text-foreground leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="flex min-h-[420px] max-h-[calc(100vh-10rem)]">
          {/* Sidebar nav */}
          <nav className="w-44 border-r border-border bg-muted/30 rounded-bl-xl py-2 flex-shrink-0 overflow-y-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary/10 text-primary font-semibold border-r-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Tab content */}
          <div className="flex-1 p-6 overflow-y-auto space-y-4">

            {/* ── ENTRA ID ── */}
            {activeTab === 'entra' && canEditPlatformSettings && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground">Entra ID App Registration</h3>
                  <button
                    onClick={handleTestConnection}
                    disabled={testStatus === 'testing'}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                      testStatus === 'testing' ? 'bg-muted text-muted-foreground border-border cursor-wait' :
                      testStatus === 'success' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700' :
                      testStatus === 'error'   ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700' :
                      'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
                    }`}
                  >
                    {testStatus === 'testing' ? 'Testing…' :
                     testStatus === 'success' ? 'Connected!' :
                     testStatus === 'error'   ? 'Failed' : 'Test Connection'}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Required app permissions: SecurityIncident.Read.All · SecurityIncident.ReadWrite.All · SecurityEvents.Read.All · User.Read.All</p>
                <div className="grid grid-cols-2 gap-3">
                  {renderField('Tenant ID',  'tenantId',  'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', { mono: true })}
                  {renderField('Client ID',  'clientId',  'App registration client ID',           { mono: true })}
                </div>
                {renderSecretField('Client Secret', 'clientSecret', 'Paste new secret to update')}

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Authentication</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {renderField('Redirect URI',  'redirectUri',  'https://your-domain.com/auth/callback', { mono: true })}
                    {renderField('CORS Origins',  'corsOrigins',  'https://your-domain.com',               { mono: true })}
                  </div>
                  <div className="mt-3">
                    {renderField('Admin Users', 'adminUsers', 'admin@domain.com, analyst@domain.com')}
                    <p className="text-[10px] text-muted-foreground mt-1">Comma-separated emails. Admin role is email-based, not Entra directory roles.</p>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Escalation</h4>
                  {renderField('Escalation Email', 'escalationEmail', 'soc-team@yourdomain.com')}
                  <p className="text-[10px] text-muted-foreground mt-1">Used as an operational contact and as the default invite reply-to address.</p>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Invite Email Sender</h4>
                      <p className="text-[11px] text-muted-foreground mt-1">Sends the branded SOC user invitation via Microsoft Graph <span className="font-mono">/users/&lt;sender&gt;/sendMail</span>. Requires application permission <span className="font-mono">Mail.Send</span> and admin consent.</p>
                    </div>
                    <span className="text-[10px] px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900 whitespace-nowrap">App-only mailbox</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {renderSelect('Invitation Email Delivery', 'inviteEmailEnabled', [
                      { value: 'false', label: 'Disabled' },
                      { value: 'true', label: 'Enabled' },
                    ])}
                    {renderField('Sender Mailbox', 'inviteSenderMailbox', 'soc-invites@yourdomain.com')}
                    {renderField('Sender Display Name', 'inviteSenderDisplayName', 'KloudSecure SOC')}
                    {renderField('Reply-To Mailbox', 'inviteReplyTo', 'soc-team@yourdomain.com')}
                    {renderField('Dashboard URL', 'invitePortalUrl', 'https://kloudsecure.yourdomain.com')}
                    {renderField('Invite Brand Name', 'inviteBrandName', 'KloudSecure')}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Recommended hardening: restrict the app registration to this mailbox using an Exchange Online application access policy.</p>
                </div>
              </>
            )}

            {/* ── AI ── */}
            {activeTab === 'ai' && canEditPlatformSettings && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground">AI Foundry / Azure OpenAI</h3>
                  <button
                    onClick={handleTestAI}
                    disabled={aiTestStatus === 'testing'}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                      aiTestStatus === 'testing' ? 'bg-muted text-muted-foreground border-border cursor-wait' :
                      aiTestStatus === 'success' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700' :
                      aiTestStatus === 'warn'    ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700' :
                      aiTestStatus === 'error'   ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700' :
                      'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
                    }`}
                  >
                    {aiTestStatus === 'testing' ? 'Testing…' :
                     aiTestStatus === 'success' ? 'Connected!' :
                     aiTestStatus === 'warn'    ? 'Needs setup' :
                     aiTestStatus === 'error'   ? 'Failed' : 'Test AI'}
                  </button>
                </div>

                {aiTestMsg && (
                  <div className={`text-xs rounded-lg px-3 py-2 border ${
                    aiTestStatus === 'success' ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' :
                    aiTestStatus === 'warn'    ? 'bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300' :
                    'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                  }`}>
                    {aiTestMsg}
                    {(aiTestStatus === 'warn') && (
                      <p className="mt-1 font-semibold">→ Go to <a href="https://ai.azure.com" target="_blank" rel="noopener noreferrer" className="underline">ai.azure.com</a> → your project → <strong>Models + endpoints</strong> → <strong>+ Deploy model</strong> → choose gpt-4o → copy the deployment name here.</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {renderField('Foundry Endpoint URL', 'foundryEndpoint', 'https://xxx.services.ai.azure.com')}
                  {renderSecretField('API Key', 'foundryKey', 'Paste new key to update')}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Model Deployment Name</label>
                  <input
                    value={form.aiModel}
                    onChange={e => set('aiModel', e.target.value)}
                    placeholder="e.g. gpt-4o (must match your Azure AI Foundry deployment name)"
                    className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Must match the exact deployment name in Azure AI Foundry → your project → Models + endpoints.</p>
                </div>

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Setup: Create a Model Deployment</h4>
                  <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                    <li>Go to <a href="https://ai.azure.com" target="_blank" rel="noopener noreferrer" className="text-primary underline font-semibold">ai.azure.com</a> and open your project</li>
                    <li>In the left sidebar click <strong>Models + endpoints</strong></li>
                    <li>Click <strong>+ Deploy model</strong> → choose <strong>gpt-4o</strong></li>
                    <li>Give it a deployment name (e.g. <code className="bg-muted px-1 rounded">gpt-4o</code>) → click <strong>Deploy</strong></li>
                    <li>Enter that deployment name in the field above, then click <strong>Test AI</strong></li>
                  </ol>
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-[10px] text-blue-700 dark:text-blue-300"><strong>Current endpoint:</strong> {form.foundryEndpoint || 'Not set'}</p>
                    <p className="text-[10px] text-blue-700 dark:text-blue-300 mt-0.5"><strong>Note:</strong> The API key for <code>kloudynet.services.ai.azure.com</code> is different from the one for <code>kloudynet.openai.azure.com</code>. Update the AZURE_OPENAI_API_KEY secret in Azure Key Vault or the KloudSecure runtime environment.</p>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">AI Capabilities</h4>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[
                      { label: 'KQL Generation', desc: 'Natural language → KQL' },
                      { label: 'Attack Stories',  desc: 'AI incident narratives' },
                      { label: 'AI Enrichment',   desc: 'Analysis + Sentinel comments' },
                    ].map(c => (
                      <div key={c.label} className="bg-muted/50 rounded-lg p-3 border border-border">
                        <div className="text-xs font-semibold text-foreground">{c.label}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{c.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── THREAT INTEL ── */}
            {activeTab === 'threatintel' && canEditPlatformSettings && (
              <>
                <h3 className="text-sm font-bold text-foreground">Threat Intelligence API Keys</h3>
                <p className="text-xs text-muted-foreground">Optional keys for IOC enrichment and reputation lookups.</p>
                <div className="space-y-3">
                  {renderSecretField('Rösti / Kloudynet TI API Key', 'rostiKey',     'Paste key to configure')}
                  {renderSecretField('VirusTotal API Key',           'vtKey',        'Paste key to configure')}
                  {renderSecretField('AbuseIPDB API Key',            'abuseipdbKey', 'Paste key to configure')}
                  {renderSecretField('OTX AlienVault API Key',       'otxKey',       'Paste key to configure')}
                  {renderSecretField('Spur API Key',                 'spurKey',      'Paste key to configure')}
                  {renderSecretField('Talos Intelligence API Key',   'talosKey',     'Paste key to configure')}
                </div>
                <p className="text-[10px] text-muted-foreground -mt-1">
                  Tip: the Rösti key can also be supplied via the <code className="font-mono">ROSTI_API_KEY</code> environment secret.
                  When both are present, the environment secret wins.
                </p>

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">API Status</h4>
                  <div className="space-y-2">
                    {[
                      { name: 'Rösti / Kloudynet TI',  key: 'rostiKey',     desc: 'Kloudynet OSINT reports, IOCs & 30-day Threat Hunt' },
                      { name: 'VirusTotal',            key: 'vtKey',        desc: 'File/URL/IP reputation lookups' },
                      { name: 'AbuseIPDB',             key: 'abuseipdbKey', desc: 'IP abuse confidence scoring' },
                      { name: 'OTX AlienVault',        key: 'otxKey',       desc: 'Community-driven IOC pulses & reputation' },
                      { name: 'Spur',                  key: 'spurKey',      desc: 'VPN / proxy / residential context for IPs' },
                      { name: 'Talos Intelligence',    key: 'talosKey',     desc: 'Cisco threat intelligence' },
                    ].map(api => {
                      const ok = !!secretsConfigured[api.key];
                      return (
                        <div key={api.name} className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-lg">
                          <div>
                            <div className="text-xs font-semibold text-foreground">{api.name}</div>
                            <div className="text-[10px] text-muted-foreground">{api.desc}</div>
                          </div>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                            ok
                              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {ok ? 'Configured' : 'Not configured'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* ── OPERATIONAL ── */}
            {activeTab === 'operational' && (
              <>
                <h3 className="text-sm font-bold text-foreground">Operational Settings</h3>
                <p className="text-xs text-muted-foreground -mt-1">
                  Read-only view of how this build is wired. Polling cadences are managed in code
                  (incident watcher: 30 s foreground / 60 s background; dashboard live data: 30 s,
                  visibility-paused) so SLA-safe defaults can&apos;t be accidentally weakened from the UI.
                </p>

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Polling &amp; Refresh</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      ['Incident watcher (foreground)', 'Every 30 s · React hook polling /incidents/global-watch'],
                      ['Incident watcher (background)', 'Every 60 s · continues when tab is hidden for SLA safety'],
                      ['Dashboard live data',           'Every 30 s · paused when tab is hidden, immediate catch-up on resume'],
                      ['SLA breach badges',             '60 s in-memory TTL + 60 s self-refresh on each rendered badge'],
                      ['Customer list',                 'React Query, refetch every 30 s (paused in background)'],
                      ['Server-side watch cache',       '20 s TTL with single-flight de-duplication per upstream'],
                    ].map(([l, v]) => (
                      <div key={l} className="bg-muted/30 rounded-lg p-2.5">
                        <div className="font-semibold text-foreground">{l}</div>
                        <div className="text-muted-foreground mt-0.5">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Auth Flow</h4>
                  {[
                    ['User login',      'Microsoft Entra (Azure AD) · OIDC Authorization Code + PKCE via @azure/msal-node'],
                    ['API calls',       'Client credentials flow (app-only token) for Sentinel / Microsoft Graph'],
                    ['Authorization',   'DB-stored RBAC roles (admin, soc_l1, soc_l2, analyst, customer_viewer) + per-customer × per-tab matrix'],
                    ['Session',         'express-session backed by Postgres (connect-pg-simple) · 12 h lifetime · HttpOnly + SameSite=Lax + Secure'],
                    ['Rate limiting',   'Global 600/min/user + stricter per-route limiters on AI / KQL / dark-web endpoints'],
                  ].map(([l, v]) => (
                    <div key={l} className="flex gap-2 text-xs mb-2">
                      <span className="font-semibold text-foreground w-32 flex-shrink-0">{l}:</span>
                      <span className="text-muted-foreground">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Deployment Info</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      ['API server',  'Node.js + Express (TypeScript) · @workspace/api-server'],
                      ['Dashboard',   'React + Vite SPA · @workspace/soc-dashboard'],
                      ['Database',    'PostgreSQL via Drizzle ORM (DATABASE_URL)'],
                      ['Hosting',     'Azure Linux VM (Ubuntu) · Nginx reverse proxy · systemd-managed API service'],
                      ['TLS',         'Let\'s Encrypt certificate managed by Certbot on the KloudSecure DNS name'],
                      ['Schedulers',  'In-process: dark-web sweeper + monthly report snapshot (toggle via env)'],
                    ].map(([l, v]) => (
                      <div key={l} className="bg-muted/30 rounded-lg p-2.5">
                        <div className="font-semibold text-foreground">{l}</div>
                        <div className="text-muted-foreground mt-0.5">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── CUSTOMERS ── */}
            {activeTab === 'customers' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-foreground">Managed Customers</h3>
                    <p className="text-xs text-muted-foreground">Add and manage multiple Azure tenants for MSSP monitoring.</p>
                  </div>
                  <button
                    onClick={() => { setEditingCustomer({ ...BLANK_CUSTOMER, color: CUSTOMER_COLORS[customers.length % CUSTOMER_COLORS.length] }); setCustError(''); }}
                    className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-lg hover:opacity-90"
                  >
                    + Add Customer
                  </button>
                </div>

                {customers.length === 0 && !editingCustomer && (
                  <div className="text-center py-10 border border-dashed border-border rounded-xl text-muted-foreground">
                    <p className="font-semibold text-sm text-foreground">No customers yet</p>
                    <p className="text-xs mt-1">Click "Add Customer" to create your first managed tenant.</p>
                  </div>
                )}

                {customers.map(c => {
                  const tr = custTestResult[c.id];
                  return (
                    <div key={c.id} className="border border-border rounded-xl overflow-hidden">
                      <div className="h-1" style={{ backgroundColor: c.color ?? '#6b7280' }} />
                      <div className="flex items-center justify-between p-3 bg-muted/30">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color ?? '#6b7280' }} />
                          <span className="font-semibold text-sm text-foreground">{c.name}</span>
                          {c.isDefault && <span className="text-[10px] bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-bold">DEFAULT</span>}
                          {c.lighthouseMode && <span className="text-[10px] bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded-full font-bold">LIGHTHOUSE</span>}
                          <span
                            className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded-full font-mono"
                            title={c.timezone ?? 'UTC'}
                          >
                            {timezoneAbbr(c.timezone)}
                          </span>
                          {!c.hasSecret && !c.isDefault && !c.lighthouseMode && <span className="text-[10px] bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 px-1.5 py-0.5 rounded-full">NO SECRET</span>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {tr && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${tr.ok ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'}`}>
                              {tr.msg}
                            </span>
                          )}
                          <button
                            onClick={() => testCustomer(c.id)}
                            disabled={custTesting === c.id}
                            className="text-xs border border-border bg-card px-2 py-1 rounded-lg hover:bg-muted disabled:opacity-60"
                          >
                            {custTesting === c.id ? 'Testing…' : 'Test'}
                          </button>
                          <button
                            onClick={() => {
                              // IMPORTANT: this handler enumerates every persisted customer
                              // field that the edit form needs to display. Any field omitted
                              // here will silently appear "reset to default" to the admin
                              // when they reopen Edit, even though the value is still saved
                              // server-side — keep this list in sync with the form below.
                              setEditingCustomer({
                                id: c.id, name: c.name, tenantId: c.tenantId ?? '', clientId: c.clientId ?? '',
                                clientSecret: '', workspaceId: c.workspaceId ?? '', workspaceName: c.workspaceName ?? '',
                                subscription: c.subscription ?? '', resourceGroup: c.resourceGroup ?? '', color: c.color ?? '#3b82f6',
                                lighthouseMode: c.lighthouseMode ?? false,
                                timezone: c.timezone ?? 'UTC',
                                triageMode: c.triageMode ?? 'off',
                                triageAutoCloseMinConfidence: c.triageAutoCloseMinConfidence ?? 90,
                                autoInvestigateMode: c.autoInvestigateMode ?? 'off',
                              });
                              setCustError('');
                            }}
                            className="text-xs border border-border bg-card px-2 py-1 rounded-lg hover:bg-muted"
                            title={c.isDefault ? 'Edit the default workspace (e.g. enable Lighthouse mode)' : 'Edit customer'}
                          >
                            Edit
                          </button>
                          {!c.isDefault && (
                            <button
                              onClick={() => deleteCustomer(c.id)}
                              className="text-xs border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 bg-card px-2 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="px-3 pb-2 pt-1 text-xs text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-0.5">
                        {c.tenantId && <div><span className="font-medium">Tenant:</span> <span className="font-mono">{c.tenantId.slice(0,8)}…</span></div>}
                        {c.workspaceId && <div><span className="font-medium">Workspace:</span> <span className="font-mono">{c.workspaceId.slice(0,8)}…</span></div>}
                      </div>
                    </div>
                  );
                })}

                {/* Add / Edit form */}
                {editingCustomer && (
                  <div className="border border-primary/40 rounded-xl bg-primary/5 overflow-hidden">
                    <div className="p-4 pb-3 space-y-3">
                    <h4 className="text-sm font-bold text-foreground">{editingCustomer.id ? 'Edit Customer' : 'New Customer'}</h4>

                    {/* Lighthouse toggle */}
                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2.5">
                      <div>
                        <div className="text-xs font-semibold text-blue-800 dark:text-blue-300">Azure Lighthouse Mode</div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                          Uses your MSSP app registration to access this customer's delegated tenant. No separate credentials needed.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingCustomer(c => c ? { ...c, lighthouseMode: !c.lighthouseMode, clientId: '', clientSecret: '' } : c)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ml-3 ${editingCustomer.lighthouseMode ? 'bg-blue-600' : 'bg-muted'}`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${editingCustomer.lighthouseMode ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-xs text-muted-foreground mb-1 block">Customer Name *</label>
                        <input value={editingCustomer.name} onChange={e => setEditingCustomer(c => c ? { ...c, name: e.target.value } : c)}
                          placeholder="Contoso Ltd." className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:border-primary" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-muted-foreground mb-1 block">Customer Tenant ID *</label>
                        <input value={editingCustomer.tenantId} onChange={e => setEditingCustomer(c => c ? { ...c, tenantId: e.target.value } : c)}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono bg-background text-foreground focus:outline-none focus:border-primary" />
                      </div>

                      {/* Credential fields — hidden in Lighthouse mode */}
                      {!editingCustomer.lighthouseMode && (
                        <>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Client ID (App Registration)</label>
                            <input value={editingCustomer.clientId ?? ''} onChange={e => setEditingCustomer(c => c ? { ...c, clientId: e.target.value } : c)}
                              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono bg-background text-foreground focus:outline-none focus:border-primary" />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Client Secret {editingCustomer.id ? '(blank = keep existing)' : ''}</label>
                            <input type="password" value={editingCustomer.clientSecret} onChange={e => setEditingCustomer(c => c ? { ...c, clientSecret: e.target.value } : c)}
                              placeholder="Enter client secret…" className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono bg-background text-foreground focus:outline-none focus:border-primary" />
                          </div>
                        </>
                      )}

                      {editingCustomer.lighthouseMode && (
                        <div className="col-span-2 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/30 rounded-lg px-3 py-2">
                          Using MSSP credentials (Client ID &amp; Secret from Entra ID settings). Ensure your MSSP app has been added as an authorized principal via Azure Lighthouse in this customer's tenant.
                        </div>
                      )}

                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Sentinel Workspace ID *</label>
                        <input value={editingCustomer.workspaceId} onChange={e => setEditingCustomer(c => c ? { ...c, workspaceId: e.target.value } : c)}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono bg-background text-foreground focus:outline-none focus:border-primary" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Workspace Name (optional)</label>
                        <input value={editingCustomer.workspaceName} onChange={e => setEditingCustomer(c => c ? { ...c, workspaceName: e.target.value } : c)}
                          placeholder="my-sentinel-workspace" className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:border-primary" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Subscription ID (optional)</label>
                        <input value={editingCustomer.subscription} onChange={e => setEditingCustomer(c => c ? { ...c, subscription: e.target.value } : c)}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono bg-background text-foreground focus:outline-none focus:border-primary" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Resource Group (optional)</label>
                        <input value={editingCustomer.resourceGroup} onChange={e => setEditingCustomer(c => c ? { ...c, resourceGroup: e.target.value } : c)}
                          placeholder="my-resource-group" className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:border-primary" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Reporting Timezone</label>
                        <select
                          value={editingCustomer.timezone ?? 'UTC'}
                          onChange={e => setEditingCustomer(c => c ? { ...c, timezone: e.target.value } : c)}
                          className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:border-primary"
                        >
                          {SUPPORTED_TIMEZONES.map((tz) => (
                            <option key={tz.id} value={tz.id}>{tz.label}</option>
                          ))}
                        </select>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Drives the Daily Report date window and timestamp display for this customer.
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Color Tag</label>
                        <div className="flex gap-1.5 flex-wrap">
                          {CUSTOMER_COLORS.map(col => (
                            <button key={col} onClick={() => setEditingCustomer(c => c ? { ...c, color: col } : c)}
                              className={`w-6 h-6 rounded-full border-2 transition-all ${editingCustomer.color === col ? 'border-foreground scale-110' : 'border-transparent'}`}
                              style={{ backgroundColor: col }} />
                          ))}
                        </div>
                      </div>

                      <div className="col-span-2 border border-border rounded-lg p-3 bg-muted/20">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <label className="text-xs font-semibold text-muted-foreground mb-1 block">Customer Logo</label>
                            <p className="text-[11px] text-muted-foreground">PNG, JPG/JPEG or WebP only. Maximum size 1 MB. Displayed next to the customer selector in the dashboard header.</p>
                          </div>
                          {editingCustomer.logoUrl ? (
                            <img src={editingCustomer.logoUrl} alt={`${editingCustomer.name || 'Customer'} logo`} className="h-12 w-12 rounded-lg object-contain bg-white border border-border p-1 shadow-sm" />
                          ) : (
                            <div className="h-12 w-12 rounded-lg border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground">Logo</div>
                          )}
                        </div>
                        {editingCustomer.id ? (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <label className="cursor-pointer px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60">
                              {logoSaving ? 'Saving logo…' : 'Upload Logo'}
                              <input
                                type="file"
                                accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                                className="hidden"
                                disabled={logoSaving}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.currentTarget.value = '';
                                  if (file) void uploadCustomerLogo(file);
                                }}
                              />
                            </label>
                            {editingCustomer.logoUrl && (
                              <button
                                type="button"
                                onClick={() => void removeCustomerLogo()}
                                disabled={logoSaving}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/30 disabled:opacity-60"
                              >
                                Remove Logo
                              </button>
                            )}
                            {editingCustomer.logoFileName && <span className="text-[11px] text-muted-foreground">Current: {editingCustomer.logoFileName}</span>}
                          </div>
                        ) : (
                          <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-300">Save the customer first to enable logo upload.</p>
                        )}
                        {logoError && <p className="mt-2 text-xs text-red-600 dark:text-red-300">{logoError}</p>}
                      </div>
                    </div>

                    {editingCustomer.id && isAdmin && (
                      <div className="border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3">
                        <div className="text-xs font-semibold text-blue-800 dark:text-blue-300">
                          Sentinel Playbooks moved to ResponseOps
                        </div>
                        <p className="text-[11px] text-blue-700/80 dark:text-blue-300/80 mt-1">
                          Use <strong>ResponseOps Studio → Discover Playbooks</strong> to scan Logic Apps across the full subscription and generate AI descriptions. Use <strong>Masters → Playbook Access Master</strong> to control who can execute each playbook per customer tenant.
                        </p>
                      </div>
                    )}

                    <div className="border border-cyan-300 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg p-3">
                      <div className="text-xs font-semibold text-cyan-800 dark:text-cyan-300">AI SOC automation settings moved</div>
                      <p className="mt-1 text-[11px] leading-relaxed text-cyan-700/80 dark:text-cyan-300/80">
                        AI SOC automation is managed under <strong>Administration → Neo Master / Guardian Master</strong>. Neo investigation, incident classification and auto-close policy are no longer edited from customer Settings.
                      </p>
                    </div>

                    {custError && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">{custError}</div>}
                    </div>

                    {/* Sticky action bar so Save Customer is always reachable, even on small viewports */}
                    <div className="sticky bottom-0 z-10 flex flex-wrap gap-2 justify-end px-4 py-3 bg-primary/10 backdrop-blur border-t border-primary/30">
                      <button onClick={() => { setEditingCustomer(null); setCustError(''); }}
                        className="border border-border bg-card text-foreground px-4 py-1.5 rounded-lg text-sm hover:bg-muted">
                        Cancel
                      </button>
                      <button onClick={saveCustomer} disabled={custSaving}
                        className="bg-primary text-primary-foreground px-4 py-1.5 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-60">
                        {custSaving ? 'Saving…' : 'Save Customer'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── SOC USERS (admin only) ── */}
            {activeTab === 'users' && isAdmin && user && (
              <UsersAdminTab currentUserId={user.id} />
            )}

            {/* ── SLA POLICIES (admin only) ── */}
            {activeTab === 'sla' && isAdmin && <SlaPolicyEditor />}

            {/* ── SUPPRESSION RULES (admin only) ── */}
            {activeTab === 'suppression' && isAdmin && <SuppressionRulesEditor />}

          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4">
          <div className="flex items-center gap-4">
            <div className={`flex-1 p-2.5 rounded-lg border text-xs ${
              isDeveloper
                ? (azureOk
                    ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                    : 'bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300')
                : 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
            }`}>
              {isDeveloper
                ? (azureOk
                    ? <><strong>Secure platform configuration loaded.</strong> Add or update managed customer workspaces to enable live Sentinel data collection.</>
                    : <><strong>Platform configuration incomplete.</strong> Enter the Azure Entra ID and integration settings above, then save to connect live services.</>)
                : <><strong>Administration mode.</strong> Customer, user, audit, SLA and suppression controls are available. Platform secrets and integration settings are restricted to Developer.</>
              }
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={onClose}
                className="border border-border bg-muted/50 text-foreground px-5 py-2 rounded-lg font-semibold text-sm hover:bg-muted"
              >
                {isDeveloper ? 'Cancel' : 'Close'}
              </button>
              {isDeveloper && (
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${
                    saveStatus === 'saving'  ? 'bg-muted text-muted-foreground cursor-wait' :
                    saveStatus === 'success' ? 'bg-green-600 text-white' :
                    saveStatus === 'error'   ? 'bg-red-600 text-white' :
                    'bg-primary text-primary-foreground hover:opacity-90'
                  }`}
                >
                  {saveBtnLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
